import { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { ShoppingBag, Plus, Minus, Trash2, ArrowRight, Loader2, ArrowDown } from 'lucide-react';
import { createCheckout } from '@/lib/api';
import { useCartStore } from '@/lib/store';
import { toast } from 'sonner';
import NewsletterForm from '@/components/NewsletterForm';

const COLOR_MAP = {
  nero: '#1c1917',
  bianco: '#fafaf9', avorio: '#fafaf9',
  blu: '#1e3a5f',
  champagne: '#d4af37', oro: '#d4af37',
  sabbia: '#c2b280',
  corallo: '#ff6f61',
};

function getColorHex(colorName) {
  const lower = colorName.toLowerCase();
  for (const [key, hex] of Object.entries(COLOR_MAP)) {
    if (lower.includes(key)) return hex;
  }
  return '#9ca3af';
}

// Product Collections
const collections = {
  donna: {
    title: 'Donna',
    subtitle: 'La Villa Collection',
    products: [
      {
        id: 'donna-1',
        name: 'La Villa Signature One-Piece',
        price: 189,
        description: 'Un intero che racconta eleganza senza tempo. Taglio sartoriale, tessuto italiano stretch che accarezza ogni curva.',
        image: '/images/lux-donna-onepiece.png',
        colors: ['Nero', 'Avorio', 'Blu Notte'],
        sizes: ['XS', 'S', 'M', 'L', 'XL'],
        category: 'donna',
      },
      {
        id: 'donna-2',
        name: 'La Villa Pampelonne Bikini',
        price: 159,
        description: 'Ispirato alle spiagge più esclusive della Costa Azzurra. Due pezzi dal fit impeccabile.',
        image: '/images/lux-donna-bikini.png',
        colors: ['Champagne', 'Corallo', 'Bianco'],
        sizes: ['XS', 'S', 'M', 'L'],
        category: 'donna',
      },
      {
        id: 'donna-3',
        name: 'La Villa Riviera Cover-Up',
        price: 129,
        description: 'Dal bordo piscina al tramonto. Un copricostume in lino che veste ogni momento.',
        image: '/images/lux-donna-coverup.png',
        colors: ['Bianco', 'Sabbia'],
        sizes: ['S/M', 'M/L'],
        category: 'donna',
      },
      {
        id: 'donna-4',
        name: 'La Villa Beach Dress',
        price: 219,
        description: 'Lino pregiato che danza con la brezza estiva. Per chi ama il lusso discreto.',
        image: '/images/lux-donna-dress.png',
        colors: ['Avorio', 'Blu Cielo'],
        sizes: ['XS', 'S', 'M', 'L'],
        category: 'donna',
      },
      {
        id: 'donna-5',
        name: 'La Villa Linen Set',
        price: 279,
        description: 'Completo due pezzi in lino italiano. Eleganza effortless dal giorno alla sera.',
        image: '/images/lux-donna-linenset.png',
        colors: ['Sabbia', 'Nero'],
        sizes: ['XS', 'S', 'M', 'L'],
        category: 'donna',
      },
    ],
  },
  uomo: {
    title: 'Uomo',
    subtitle: 'La Villa Collection',
    products: [
      {
        id: 'uomo-1',
        name: 'La Villa Riviera Swim Short',
        price: 119,
        description: 'Costume da bagno in tessuto quick-dry con dettagli sartoriali. Il gentleman del poolside.',
        image: '/images/lux-uomo-swimshort.png',
        colors: ['Blu Navy', 'Verde Oliva', 'Nero'],
        sizes: ['S', 'M', 'L', 'XL', 'XXL'],
        category: 'uomo',
      },
      {
        id: 'uomo-2',
        name: 'La Villa Summer Shirt',
        price: 149,
        description: 'Camicia in cotone egiziano. Fresca, impeccabile, per ogni aperitivo al tramonto.',
        image: '/images/lux-uomo-summershirt.png',
        colors: ['Bianco', 'Azzurro', 'Rosa Cipria'],
        sizes: ['S', 'M', 'L', 'XL'],
        category: 'uomo',
      },
      {
        id: 'uomo-3',
        name: 'La Villa Linen Shirt',
        price: 169,
        description: 'Lino italiano al suo meglio. Eleganza naturale che respira con te.',
        image: '/images/lux-uomo-linenshirt.png',
        colors: ['Sabbia', 'Bianco', 'Blu'],
        sizes: ['S', 'M', 'L', 'XL'],
        category: 'uomo',
      },
      {
        id: 'uomo-4',
        name: 'La Villa Resort Polo',
        price: 129,
        description: 'Polo in piqué premium. Per chi non scende a compromessi, nemmeno in vacanza.',
        image: '/images/lux-uomo-polo.png',
        colors: ['Nero', 'Bianco', 'Champagne'],
        sizes: ['S', 'M', 'L', 'XL'],
        category: 'uomo',
      },
      {
        id: 'uomo-5',
        name: 'La Villa Pool Short',
        price: 99,
        description: 'Bermuda in terry cotton. Comfort assoluto dal lettino al bar.',
        image: '/images/lux-uomo-poolshort.png',
        colors: ['Grigio', 'Navy', 'Bianco'],
        sizes: ['S', 'M', 'L', 'XL'],
        category: 'uomo',
      },
    ],
  },
  accessori: {
    title: 'Accessori',
    subtitle: 'La Villa',
    products: [
      {
        id: 'acc-1',
        name: 'La Villa Sun Towel',
        price: 89,
        description: 'Telo mare in cotone jacquard. Morbidezza e stile firmati La Villa.',
        image: '/images/lux-acc-towel.png',
        colors: ['Avorio/Oro', 'Blu/Bianco'],
        sizes: ['Unica'],
        category: 'accessori',
      },
      {
        id: 'acc-2',
        name: 'La Villa Club Cap',
        price: 59,
        description: 'Cappellino in cotone organico. Il tuo pass per il club.',
        image: '/images/lux-acc-cap.png',
        colors: ['Nero', 'Bianco', 'Navy'],
        sizes: ['Unica'],
        category: 'accessori',
      },
      {
        id: 'acc-3',
        name: 'La Villa Straw Hat',
        price: 79,
        description: 'Cappello in paglia naturale intrecciato a mano. Ombra elegante.',
        image: '/images/lux-acc-strawhat.png',
        colors: ['Naturale', 'Nero'],
        sizes: ['S/M', 'M/L'],
        category: 'accessori',
      },
      {
        id: 'acc-4',
        name: 'La Villa Thermal Bottle',
        price: 49,
        description: 'Borraccia termica in acciaio. Idratazione con stile, sempre con te.',
        image: '/images/lux-acc-bottle.png',
        colors: ['Nero Opaco', 'Oro Rosa', 'Argento'],
        sizes: ['500ml'],
        category: 'accessori',
      },
      {
        id: 'acc-5',
        name: 'La Villa Beach Bag',
        price: 149,
        description: 'Borsa mare in rafia intrecciata. Capiente, elegante, indispensabile.',
        image: '/images/lux-acc-beachbag.png',
        colors: ['Naturale', 'Nero'],
        sizes: ['Unica'],
        category: 'accessori',
      },
      {
        id: 'acc-6',
        name: 'La Villa Sunglasses',
        price: 199,
        description: 'Occhiali da sole con montatura in acetato italiano. Protezione UV400.',
        image: '/images/lux-acc-sunglasses.png',
        colors: ['Tartaruga', 'Nero', 'Champagne'],
        sizes: ['Unica'],
        category: 'accessori',
      },
      {
        id: 'acc-7',
        name: 'La Villa Tote',
        price: 129,
        description: 'Tote bag in canvas premium. Per chi porta La Villa ovunque.',
        image: '/images/lux-acc-tote.png',
        colors: ['Nero', 'Sabbia'],
        sizes: ['Unica'],
        category: 'accessori',
      },
    ],
  },
};

// Product Card Component
function ProductCard({ product, onAddToCart }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      className="group cursor-pointer"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`product-${product.id}`}
    >
      <Link to={`/shop/${product.id}`}>
        <div className="aspect-[3/4] overflow-hidden bg-stone-100 mb-6 relative">
          <motion.img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
            animate={{ scale: isHovered ? 1.05 : 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
          <motion.div
            className="absolute inset-0 bg-black/20"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </Link>
      
      <div className="space-y-2">
        <Link to={`/shop/${product.id}`}>
          <h3 className="font-body text-sm tracking-wide text-night group-hover:text-stone-600 transition-colors">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center gap-2">
          <span className="font-body text-sm text-stone-400 blur-[3px] select-none">€{product.price}</span>
          <span className="font-body text-xs uppercase tracking-wider text-champagne bg-champagne/10 px-2 py-0.5">TBA</span>
        </div>
        
        {/* Color dots */}
        <div className="flex gap-2 pt-2">
          {product.colors.slice(0, 3).map((color) => (
            <span
              key={color}
              className="w-3 h-3 rounded-full border border-stone-300"
              style={{
                backgroundColor: getColorHex(color)
              }}
              title={color}
            />
          ))}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          className="text-xs uppercase tracking-widest text-stone-600 hover:text-night p-0 h-auto mt-3"
          onClick={(e) => {
            e.preventDefault();
            onAddToCart(product);
          }}
          data-testid={`add-to-cart-${product.id}`}
        >
          <Plus size={14} className="mr-1" />
          Aggiungi
        </Button>
      </div>
    </motion.div>
  );
}

export default function Shop() {
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [email, setEmail] = useState('');
  const heroRef = useRef(null);
  
  const { items, addItem, removeItem, updateQuantity, getTotal, getItemCount } = useCartStore();
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const handleAddToCart = (product) => {
    addItem(product, 1, product.sizes[0], product.colors[0]);
    toast.success('Aggiunto al carrello');
  };

  const handleCheckout = async () => {
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    if (items.length === 0) {
      toast.error('Il carrello è vuoto');
      return;
    }
    
    setCheckoutLoading(true);
    try {
      const cartItems = items.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
        size: item.size,
        color: item.color,
      }));
      
      const response = await createCheckout(cartItems, email);
      window.location.href = response.url;
    } catch (error) {
      toast.error('Errore durante il checkout. Riprova.');
      setCheckoutLoading(false);
    }
  };

  const scrollToCollection = () => {
    document.getElementById('donna-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white min-h-screen relative" data-testid="shop-page">
      {/* Coming Soon Overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
        <div className="absolute inset-0 backdrop-blur-md bg-white/30" />
        <motion.div
          className="relative text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h2 className="font-heading text-6xl md:text-8xl lg:text-9xl text-night tracking-tight">
            Soon...
          </h2>
          <p className="font-body text-lg md:text-xl text-night/70 mt-4 uppercase tracking-widest">
            La Villa Collection
          </p>
        </motion.div>
      </div>

      {/* Hero Full Screen */}
      <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
            src="/images/la-collezione.png"
            alt="La Villa Collection"
            className="w-full h-full object-cover scale-110"
          />
          <div className="absolute inset-0 bg-black/30" />
        </motion.div>
        
        <motion.div 
          className="relative z-10 text-center px-6 max-w-4xl mx-auto"
          style={{ opacity: heroOpacity }}
        >
          <motion.p
            className="font-body text-xs uppercase tracking-[0.4em] text-white/80 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Summer 2026
          </motion.p>
          
          <motion.h1
            className="font-heading text-5xl md:text-7xl lg:text-8xl text-white tracking-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            data-testid="page-title"
          >
            La Villa Collection
          </motion.h1>
          
          <motion.p
            className="font-accent text-xl md:text-2xl italic text-white/90 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Celebration of Summer.
          </motion.p>
          
          <motion.p
            className="font-body text-sm uppercase tracking-[0.3em] text-white/70 mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Essentials for the Season.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Button
              onClick={scrollToCollection}
              className="bg-white text-night hover:bg-stone-100 rounded-none px-12 py-6 text-sm uppercase tracking-widest"
              data-testid="hero-cta"
            >
              Shop Now
            </Button>
          </motion.div>
        </motion.div>
        
        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <ArrowDown className="text-white/60" size={24} />
          </motion.div>
        </motion.div>
        
        {/* Cart Button - Fixed */}
        <Sheet open={cartOpen} onOpenChange={setCartOpen}>
          <SheetTrigger asChild>
            <Button
              className="fixed top-6 right-6 z-50 bg-white text-night hover:bg-stone-100 rounded-none px-6 py-3"
              data-testid="cart-button"
            >
              <ShoppingBag size={18} className="mr-2" />
              ({getItemCount()})
            </Button>
          </SheetTrigger>
          <SheetContent className="bg-white w-full sm:max-w-md border-l border-stone-200">
            <SheetHeader>
              <SheetTitle className="font-heading text-2xl text-night">Il Tuo Carrello</SheetTitle>
            </SheetHeader>
            
            <div className="flex flex-col h-full pt-6">
              {items.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <p className="font-body text-stone-500">Il carrello è vuoto</p>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-auto space-y-6">
                    {items.map((item, index) => (
                      <div
                        key={`${item.product.id}-${item.size}-${item.color}`}
                        className="flex gap-4 pb-6 border-b border-stone-100"
                        data-testid={`cart-item-${index}`}
                      >
                        <div className="w-24 h-32 bg-stone-100 flex-shrink-0">
                          <img
                            src={item.product.image}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="font-body text-sm text-night tracking-wide">
                            {item.product.name}
                          </p>
                          {(item.size || item.color) && (
                            <p className="font-body text-xs text-stone-500 mt-1">
                              {item.size && `${item.size}`}
                              {item.size && item.color && ' / '}
                              {item.color && `${item.color}`}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="font-body text-sm text-stone-400 blur-[3px] select-none">€{item.product.price}</span>
                            <span className="font-body text-[10px] uppercase tracking-wider text-champagne">TBA</span>
                          </div>
                          <div className="flex items-center gap-3 mt-4">
                            <button
                              onClick={() => updateQuantity(index, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center border border-stone-200 hover:bg-stone-50"
                              data-testid={`decrease-${index}`}
                            >
                              <Minus size={12} />
                            </button>
                            <span className="font-body text-sm w-6 text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(index, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center border border-stone-200 hover:bg-stone-50"
                              data-testid={`increase-${index}`}
                            >
                              <Plus size={12} />
                            </button>
                            <button
                              onClick={() => removeItem(index)}
                              className="ml-auto text-stone-400 hover:text-night"
                              data-testid={`remove-${index}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-6 border-t border-stone-200 space-y-6 pb-8">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-sm uppercase tracking-widest text-stone-600">Totale</span>
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-2xl text-stone-400 blur-[4px] select-none">€{getTotal().toFixed(2)}</span>
                        <span className="font-body text-xs uppercase tracking-wider text-champagne bg-champagne/10 px-2 py-1">TBA</span>
                      </div>
                    </div>
                    
                    <Input
                      type="email"
                      placeholder="La tua email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-none px-4 py-6 border-stone-200"
                      data-testid="checkout-email"
                    />
                    
                    <Button
                      disabled={true}
                      className="w-full bg-stone-400 text-white rounded-none py-6 text-sm uppercase tracking-widest cursor-not-allowed"
                      data-testid="checkout-button"
                    >
                      Presto Disponibile
                    </Button>
                    
                    <p className="font-body text-xs text-center text-stone-500">
                      Lo shop aprirà presto. Resta aggiornato!
                    </p>
                  </div>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </section>
      
      {/* Donna Section */}
      <section id="donna-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              {collections.donna.subtitle}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight">
              {collections.donna.title}
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-12">
            {collections.donna.products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Editorial Image */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            className="aspect-[4/3] md:aspect-[16/9] overflow-hidden rounded-lg"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <img
              src="/images/lux-acc-towel.png"
              alt="La Villa Lifestyle"
              className="w-full h-full object-contain bg-stone-100"
            />
          </motion.div>
        </div>
      </section>
      
      {/* Uomo Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-stone-50">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              {collections.uomo.subtitle}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight">
              {collections.uomo.title}
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-12">
            {collections.uomo.products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Brand Story */}
      <section className="py-12 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto text-center">
          <motion.p
            className="font-body text-xs uppercase tracking-[0.3em] text-stone-500 mb-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            The Philosophy
          </motion.p>
          <motion.h2
            className="font-heading text-4xl md:text-5xl text-night tracking-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Crafted for the Season.
          </motion.h2>
          <motion.div
            className="space-y-2"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <p className="font-body text-lg text-stone-600 leading-relaxed">
              La Villa non è solo un luogo.
            </p>
            <p className="font-body text-lg text-stone-600 leading-relaxed">
              È uno stile da indossare.
            </p>
            <p className="font-accent text-2xl italic text-night mt-4">
              "Ogni gesto, una firma."
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Accessori Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-stone-50">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              {collections.accessori.subtitle}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight">
              {collections.accessori.title}
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
            {collections.accessori.products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Newsletter Premium */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-night">
        <div className="max-w-xl mx-auto text-center">
          <motion.p
            className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Esclusiva
          </motion.p>
          <motion.h2
            className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Entra nel Club
          </motion.h2>
          <motion.p
            className="font-body text-stone-400 mb-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Iscriviti per accesso anticipato alle nuove collezioni, eventi esclusivi e promozioni riservate.
          </motion.p>
          <NewsletterForm />
        </div>
      </section>
      
      {/* Bottom padding for mobile CTA */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
